import useProductStore from '@/store/product'
import useSettingStore from '@/store/setting'
import useCustomizationStore from '@/store/customization'
import useAppendComponent from '@/composables/appendComponent'
import { useRoute } from 'vue-router'
import { appendChildElementIntoParentElement } from '@/utils/dom'
import { extractProductSKUFromHandle, getRawIdFromShopifyId, isProductHasTags } from '@/utils/product'
import { computed, createApp, defineComponent, getCurrentInstance, h, nextTick, ref, shallowRef, watch } from 'vue'
import {
  CUSTOMILY_FILE_INPUT_WRAPPER_CLASS,
  CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR,
  INPUT_FILE_CUSTOMILY_SELECTOR,
  QUERY_STRING_NO_CACHE,
  REMOVE_BUTTON_CUSTOMILY_ATTRIBUTE,
  REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
  SELECT_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
  SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX,
  SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX,
  INPUT_CALENDAR_CUSTOMILY_SELECTOR,
  HELP_TEXT_CUSTOMILY_SELECTOR,
  CL_CONTAINER_CUSTOMILY_SELECTOR,
  PRODUCT_TAG_CUSTOM_CROSS_WORDS,
  PRODUCT_TAG_CUSTOM_PHOTO_WORD,
} from '@/utils'
import CustomUploadImageStyleTransfer from '../CustomUploadImageStyleTransfer.vue'
import CustomMusicTransfer from '../CustomMusicTransfer.vue'
import CustomCalendar from '../CustomCalendar.vue'
import CustomUploadImageFaceSwap from '../CustomUploadImageFaceSwap.vue'
import CustomUploadQrCodeSoundWave from '../CustomUploadQrCodeSoundWave.vue'
import CustomCrossWords from '../CustomCrossWords.vue'
import CustomPhotoWord from '../CustomPhotoWord.vue'
import Logger from '@/services/log'
import Analytics from '@/services/analytics'

export const useProductCustomization = (options?: { registerVariantWatcher?: boolean }) => {
  // Chỉ instance "host" (Customization.vue) được theo dõi đổi Type để fetch + re-render.
  // Component con (CustomUploadImageStyleTransfer) truyền false để không tự đăng ký watch,
  // tránh vòng lặp mount lồng nhau khiến Personalization bị duplicate và nhân API.
  const registerVariantWatcher = options?.registerVariantWatcher !== false
  const productStore = useProductStore()
  const settingStore = useSettingStore()
  const route = useRoute()
  const customizationStore = useCustomizationStore()
  const { appContext } = getCurrentInstance()
  const { renderComponent } = useAppendComponent()

  const percentProcessed = ref(0)
  let interval: any = null
  let multiPoseApp: ReturnType<typeof createApp> | null = null
  const multiPoseTemplate = shallowRef<any>(null)
  const STYLE_TRANSFER_OPTION_TYPE = 'style_transfer'
  const FACE_SWAP_OPTION_TYPE = 'face_swap'
  const QRCODE_SOUND_OPTION_TYPE = 'qrcode_sound'
  const CALENDAR_OPTION_TYPE = 'calendar'
  const CROSS_WORDS_OPTION_TYPE = 'cross_words'
  const PHOTO_WORD_OPTION_TYPE = 'photo_word'

  const isCustomStyleTransfer = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productStyleTransferTag || '',
    ])
  )

  const isCustomFaceSwap = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productFaceSwapTag || '',
    ])
  )

  const isCustomQrCodeSoundWave = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productQrCodeSoundWave || '',
    ])
  )

  const isCustomCalendar = computed(() =>
    isProductHasTags(productStore.product, [
      settingStore.shop?.productTagCalendar || '',
    ])
  )

  const isCustomCrossWords = computed(() =>
    isProductHasTags(productStore.product, [
      PRODUCT_TAG_CUSTOM_CROSS_WORDS || '',
    ])
  )

  const isCustomPhotoWord = computed(() =>
    isProductHasTags(productStore.product, [
      PRODUCT_TAG_CUSTOM_PHOTO_WORD || '',
    ])
  )

  const isCustomUpload = computed(
    () =>
      isCustomStyleTransfer.value ||
      isCustomFaceSwap.value ||
      isCustomQrCodeSoundWave.value ||
      isCustomCalendar.value ||
      isCustomCrossWords.value ||
      isCustomPhotoWord.value
  )

  async function fetchCustomizationTemplates() {
    if (!productStore.product || !isCustomUpload.value) return

    let codePromptAI
    if (isProductHasTags(productStore.product, ['AI_multi_pose'])) {
      const config = settingStore.baseProductPage?.productOptionsAISettings?.find(
        (item: any) => item.handle == productStore.product?.handle
      )
      if (config?.optionTitle && config?.options?.length) {
        const currentType = productStore.selectedVariant?.selectedOptions?.find(
          (o: any) => o.name === config.optionTitle
        )?.value
        const matchedEntry = config.options.find((o: any) => o.name === currentType)
        if (matchedEntry) {
          codePromptAI = matchedEntry.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
        }
      }
    } else if (isCustomStyleTransfer.value && (
      isProductHasTags(productStore.product, ['AI_removebg']) ||
      isProductHasTags(productStore.product, ['AI_QRMusic']) ||
      isProductHasTags(productStore.product, ['AI_Music']) ||
      isProductHasTags(productStore.product, ['AI_video'])
    )) {
      const config = settingStore.baseProductPage?.productOptionsAISettings?.find(
        (item: any) => item.handle == productStore.product?.handle
      )
      if (config && config?.options?.length) {
        codePromptAI = config?.options[0]?.template
      }
    }
  
    const payload = {
      code: codePromptAI ? `${extractProductSKUFromHandle(productStore.product.handle)}_${codePromptAI}` : extractProductSKUFromHandle(productStore.product.handle),
      type: productStore.product.productType || '',
      tag: productStore.product.tags?.join('|') || '',
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    await customizationStore.fetchCustomizationTemplates(payload)
  }

  async function fetchCustomizationTemplatesByCode(codePromptAI: string) {
    if (!productStore.product || !isCustomUpload.value) return
    const payload = {
      code: codePromptAI ? `${extractProductSKUFromHandle(productStore.product.handle)}_${codePromptAI}` : extractProductSKUFromHandle(productStore.product.handle),
      type: productStore.product.productType || '',
      tag: productStore.product.tags?.join('|') || '',
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    await customizationStore.fetchCustomizationTemplates(payload)
  }

  if (registerVariantWatcher) watch(
    () => productStore.selectedVariant,
    async (newVariant, oldVariant) => {
      if (!newVariant) return
      if (!isProductHasTags(productStore.product, ['AI_multi_pose'])) return

      const config = settingStore.baseProductPage?.productOptionsAISettings?.find(
        (item: any) => item.handle === productStore.product?.handle
      )
      if (!config?.optionTitle || !config?.options?.length) return

      const getTypeValue = (v: any) =>
        v?.selectedOptions?.find((o: any) => o.name === config.optionTitle)?.value

      const newType = getTypeValue(newVariant)
      const oldType = getTypeValue(oldVariant)

      if (newType && newType === oldType) return

      const matchedEntry = config.options.find((o: any) => o.name === newType)
      if (!matchedEntry) return

      const templateKey = matchedEntry.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      await fetchCustomizationTemplatesByCode(templateKey)
      await nextTick()
      setTimeout(() => { appendCustomUploadImage() }, 200)
    }
  )

  async function appendCustomUploadImage() {
    if (
      !customizationStore.customizationTemplates?.length ||
      !isCustomUpload.value
    )
      return

    await nextTick()

    const numberCustomTag = getNunberCustomTag()

    customizationStore.customizationTemplates.forEach(      
      async (customizationTemplate, index) => {
        const templates = customizationTemplate.templates

        const isMusic = customizationTemplate.is_music
        if (isMusic) {
          const templateId = customizationTemplate.id
          templates.forEach(async (template, key) => {
            const customilySetWrapper = document.querySelector('.customily-set')
            if (!customilySetWrapper) return

            if (template?.custom_fields?.length == 0) return

            const check = document.querySelector(
              `#customily-music-` + templateId + '-' + key
            )
            if (check) return

            const timestamp = Date.now()

            // create element and append to first index upload section customily
            const customeElement = document.createElement('div')
            customeElement.setAttribute(
              'id',
              `customily-music-` + templateId + '-' + key
            )
            customeElement.setAttribute(
              'class',
              `customily_music_set-visibility`
            )
            appendChildElementIntoParentElement(
              customilySetWrapper,
              customeElement,
              10
            )
            await nextTick()

            const element = document.querySelector(
              `#customily-music-` + templateId + '-' + key
            )
            if (!element) return

            const renderComponentMusicTransfer = () =>
              renderComponent({
                element,
                component: CustomMusicTransfer,
                props: {
                  templateId: templateId,
                  customFields: template?.custom_fields
                },
                appContext,
              })

            renderComponentMusicTransfer()
          })
          
          return
        }

        if (templates.length > 1) {
          templates.forEach(async (template, key) => {
            const isMusic = template.is_music
            if (isMusic) {
              const templateId = customizationTemplate.id
              const jsonId = template.template_id
              const afterSortId = template.music_after_sort_id || 10
              const customilySetWrapper = document.querySelector('.customily_set-visibility')
              if (!customilySetWrapper) return

              applyOptionSortOrder(customilySetWrapper as HTMLElement)

              customilySetWrapper.classList.add('customily-set-order-item')
              customilySetWrapper.style.display = 'flex'
              customilySetWrapper.style.flexDirection = 'column'

              const customilyOptionHtml = document.querySelector('#customily-options')
              if (!customilyOptionHtml) return
              customilyOptionHtml.classList.add('image_music')

              if (template?.custom_fields?.length == 0) return

              const check = document.querySelector(
                `#customily-music-` + templateId + '-' + key
              )
              if (check) return

              const timestamp = Date.now()

              // create element and append to first index upload section customily
              const customeElement = document.createElement('div')
              customeElement.setAttribute(
                'id',
                `customily-music-` + templateId + '-' + key
              )
              customeElement.setAttribute(
                'class',
                `customily_music_set-visibility`
              )

              customeElement.style.setProperty('--music-order', String(afterSortId))
              customilySetWrapper.appendChild(customeElement)

              await nextTick()

              const element = document.querySelector(
                `#customily-music-` + templateId + '-' + key
              )
              if (!element) return

              const renderComponentMusicTransfer = () =>
                renderComponent({
                  element,
                  component: CustomMusicTransfer,
                  props: {
                    templateId: templateId,
                    customFields: template?.custom_fields,
                    jsonId: jsonId,
                    spaceBottom: template?.space_bottom,
                  },
                  appContext,
                })

              renderComponentMusicTransfer()
              
              return
            }

            const option_sort_id = template.option_sort_id

            const customilyOptionWrapper = document.querySelector(
              `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
                'option_sort_id',
                option_sort_id
              )}`
            )
            if (!customilyOptionWrapper) return

            if (
              (numberCustomTag <= 1 &&
                !isCustomStyleTransfer.value &&
                !isCustomPhotoWord.value) ||
              (numberCustomTag > 1 &&
                customizationTemplate.option_type != STYLE_TRANSFER_OPTION_TYPE &&
                customizationTemplate.option_type != PHOTO_WORD_OPTION_TYPE)
            ) {
              const lableOption = customilyOptionWrapper.querySelector(`label`)
              if (lableOption) lableOption.style.display = 'none'
            }

            const customilyElementWrapper = customilyOptionWrapper.querySelector(
              `.${
                isCustomCalendar.value
                  ? 'cl-option-content'
                  : CUSTOMILY_FILE_INPUT_WRAPPER_CLASS
              }`
            )
          
            if (!customilyElementWrapper) return

            let isDynamicAIPrompt = isProductHasTags(productStore.product, ['AI_multi_pose'])

            if (
              isProductHasTags(productStore.product, ['AI_removebg']) ||
              isProductHasTags(productStore.product, ['AI_QRMusic']) ||
              isProductHasTags(productStore.product, ['AI_Music']) ||
              isProductHasTags(productStore.product, ['AI_video'])
            ) {
              const config = settingStore.baseProductPage?.productOptionsAISettings?.find(
                (item: any) => item.handle == productStore.product?.handle
              )
              if (config && config?.options?.length) {
                isDynamicAIPrompt = true
              }
            }

            if (isDynamicAIPrompt) {
              const appendedUploadImageDiv = customilyElementWrapper.querySelector(
                `#${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-0`
              )
              if (appendedUploadImageDiv) {
                appendedUploadImageDiv.remove()
                await nextTick()
              }
            }

            // if customization component appended, we do not anything
            const isAppendedUploadImage = customilyElementWrapper.querySelector(
              `.${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
            )
            if (!isDynamicAIPrompt && isAppendedUploadImage) return

            // hide input calendar customily
            const inputCalendarCustomily: HTMLElement | null =
              customilyElementWrapper.querySelector(
                INPUT_CALENDAR_CUSTOMILY_SELECTOR
              )
            if (inputCalendarCustomily) {
              inputCalendarCustomily.style.display = 'none'
            }

            // hide select image button and remove image button customily
            const selectImageButtonCustomily: HTMLElement | null =
              customilyElementWrapper.querySelector(
                SELECT_IMAGE_BUTTON_CUSTOMILY_SELECTOR
              )
            if (selectImageButtonCustomily) {
              selectImageButtonCustomily.style.display = 'none'
            }

            const removeImageButtonCustomily =
              customilyElementWrapper.querySelector(
                REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
              )
            customilyElementWrapper.lastElementChild!.setAttribute(
              REMOVE_BUTTON_CUSTOMILY_ATTRIBUTE,
              'true'
            )
            const inputFileCustomily = customilyElementWrapper.querySelector(
              INPUT_FILE_CUSTOMILY_SELECTOR
            )

            // hide close button customily and help text upload image for custom cross word product or photo word product
            if (isCustomCrossWords.value || isCustomPhotoWord.value) {
              const helpText = customilyOptionWrapper.querySelector(
                HELP_TEXT_CUSTOMILY_SELECTOR
              )
              if (helpText) helpText.style.display = 'none'

              const clContainerCustomily = customilyOptionWrapper.querySelector(
                `.${CL_CONTAINER_CUSTOMILY_SELECTOR}`
              )
              if (clContainerCustomily) clContainerCustomily.style.display = 'none'
            }

            // create element and append to first index upload section customily
            const customeElement = document.createElement('div')
            customeElement.setAttribute(
              'id',
              `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${key}`
            )
            customeElement.setAttribute(
              'class',
              `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
            )
            appendChildElementIntoParentElement(
              customilyElementWrapper,
              customeElement,
              0
            )
            await nextTick()


            // append component into DOM
            const element = document.querySelector(
              `#${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${key}`
            )
            if (!element) return

            const renderComponentStyleTransfer = () =>
              renderComponent({
                element,
                component: CustomUploadImageStyleTransfer,
                props: {
                  id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${key}`,
                  inputFileCustomily,
                  selectImageButtonCustomily,
                  removeImageButtonCustomily,
                  customizationTemplate,
                  optionSortId: template.option_sort_id,
                  templateId: template.template_id,
                  customName: template?.custom_name,
                  customilyElementWrapper,
                  images: template?.images,
                  custom: template?.custom,
                  onlyText: template?.only_text ? true : false,
                  isVideo: template?.is_video ? true : false,
                },
                appContext,
              })

            const renderComponentFaceSwap = () =>
              renderComponent({
                element,
                component: CustomUploadImageFaceSwap,
                props: {
                  id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${key}`,
                  inputFileCustomily,
                  selectImageButtonCustomily,
                  optionSortId: template.option_sort_id,
                  templates: customizationTemplate.templates,
                },
                appContext,
              })

            const renderComponentQrCodeSoundWave = () =>
              renderComponent({
                element,
                component: CustomUploadQrCodeSoundWave,
                props: {
                  id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${key}`,
                  customizationTemplate:
                    template.extended_settings,
                },
                appContext,
              })

            const renderComponentCalander = () =>
              renderComponent({
                element,
                component: CustomCalendar,
                props: {
                  dayIds: template.day_ids,
                  monthId: template.month_id,
                  yearId: template.year_id,
                },
                appContext,
              })

            const renderComponentCrossWords = () =>
              renderComponent({
                element,
                component: CustomCrossWords,
                props: {
                  inputFileCustomily,
                  customizationTemplate: template,
                  customilyOptionWrapper,
                },
                appContext,
              })

            const renderComponentPhotoWord = () =>
              renderComponent({
                element,
                component: CustomPhotoWord,
                props: {
                  inputFileCustomily,
                  customizationTemplate: template,
                  customilyOptionWrapper,
                  optionSortId: template.option_sort_id,
                },
                appContext,
              })

            if (numberCustomTag > 1) {
              if (
                isCustomStyleTransfer.value &&
                customizationTemplate.option_type == STYLE_TRANSFER_OPTION_TYPE
              ) {
                renderComponentStyleTransfer()
              }
              if (
                isCustomFaceSwap.value &&
                customizationTemplate.option_type == FACE_SWAP_OPTION_TYPE
              ) {
                renderComponentFaceSwap()
              }
              if (
                isCustomQrCodeSoundWave.value &&
                customizationTemplate.option_type == QRCODE_SOUND_OPTION_TYPE
              ) {
                renderComponentQrCodeSoundWave()
              }
              if (
                isCustomCalendar.value &&
                customizationTemplate.option_type == CALENDAR_OPTION_TYPE
              ) {
                renderComponentCalander()
              }

              if (
                isCustomCrossWords.value &&
                customizationTemplate.option_type == CROSS_WORDS_OPTION_TYPE
              ) {
                renderComponentCrossWords()
              }

              if (
                isCustomPhotoWord.value &&
                customizationTemplate.option_type == PHOTO_WORD_OPTION_TYPE
              ) {
                renderComponentPhotoWord()
              }
            } else {
              if (isCustomStyleTransfer.value) {
                renderComponentStyleTransfer()
              } else if (isCustomFaceSwap.value) {
                renderComponentFaceSwap()
              } else if (isCustomQrCodeSoundWave.value) {
                renderComponentQrCodeSoundWave()
              } else if (isCustomCalendar.value) {
                renderComponentCalander()
              } else if (isCustomCrossWords.value) {
                renderComponentCrossWords()
              } else if (isCustomPhotoWord.value) {
                renderComponentPhotoWord()
              }
            }
            
          })
        } else {
          const customilyOptionWrapper = document.querySelector(
            `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
              'option_sort_id',
              customizationTemplate.option_sort_id
            )}`
          )
          if (!customilyOptionWrapper) return

          // AI_multi_pose: nếu component đã mount, cập nhật dữ liệu tại chỗ.
          // Không xóa/tạo DOM → Customily không re-initialize → không gọi identify.
          // Check thêm DOM node vì forEach chạy bất đồng bộ, nhiều item cùng vào đây
          // trước khi multiPoseApp kịp gán → tránh mount trùng.
          if (
            isProductHasTags(productStore.product, ['AI_multi_pose']) &&
            (multiPoseApp ||
              customilyOptionWrapper.querySelector(
                `.${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
              ))
          ) {
            multiPoseTemplate.value = customizationTemplate
            return
          }

          if (
            (numberCustomTag <= 1 &&
              !isCustomStyleTransfer.value &&
              !isCustomPhotoWord.value) ||
            (numberCustomTag > 1 &&
              customizationTemplate.option_type != STYLE_TRANSFER_OPTION_TYPE &&
              customizationTemplate.option_type != PHOTO_WORD_OPTION_TYPE)
          ) {
            const lableOption = customilyOptionWrapper.querySelector(`label`)
            if (lableOption) lableOption.style.display = 'none'
          }

          const customilyElementWrapper = customilyOptionWrapper.querySelector(
            `.${
              isCustomCalendar.value
                ? 'cl-option-content'
                : CUSTOMILY_FILE_INPUT_WRAPPER_CLASS
            }`
          )
        
          if (!customilyElementWrapper) return

          // AI_multi_pose always needs re-render on template change — bypass dedup check via tag directly
          let isDynamicAIPrompt = isProductHasTags(productStore.product, ['AI_multi_pose'])

          if (
              isProductHasTags(productStore.product, ['AI_removebg']) ||
              isProductHasTags(productStore.product, ['AI_QRMusic']) ||
              isProductHasTags(productStore.product, ['AI_Music']) ||
              isProductHasTags(productStore.product, ['AI_video'])
            ) {
            const config = settingStore.baseProductPage?.productOptionsAISettings?.find(
              (item: any) => item.handle == productStore.product?.handle
            )
            if (config && config?.options?.length) {
              isDynamicAIPrompt = true
            }
          }

          if (isDynamicAIPrompt) {
            const appendedUploadImageDiv = customilyElementWrapper.querySelector(
              `#${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-0`
            )
            if (appendedUploadImageDiv) {
              appendedUploadImageDiv.remove()
              await nextTick()
            }
          }

          // if customization component appended, we do not anything
          const isAppendedUploadImage = customilyElementWrapper.querySelector(
            `.${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
          )
          if (!isDynamicAIPrompt && isAppendedUploadImage) return
      
          // hide input calendar customily
          const inputCalendarCustomily: HTMLElement | null =
            customilyElementWrapper.querySelector(
              INPUT_CALENDAR_CUSTOMILY_SELECTOR
            )
          if (inputCalendarCustomily) {
            inputCalendarCustomily.style.display = 'none'
          }

          // hide select image button and remove image button customily
          const selectImageButtonCustomily: HTMLElement | null =
            customilyElementWrapper.querySelector(
              SELECT_IMAGE_BUTTON_CUSTOMILY_SELECTOR
            )
          if (selectImageButtonCustomily) {
            selectImageButtonCustomily.style.display = 'none'
          }
          const removeImageButtonCustomily =
            customilyElementWrapper.querySelector(
              REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
            )
          customilyElementWrapper.lastElementChild!.setAttribute(
            REMOVE_BUTTON_CUSTOMILY_ATTRIBUTE,
            'true'
          )
          const inputFileCustomily = customilyElementWrapper.querySelector(
            INPUT_FILE_CUSTOMILY_SELECTOR
          )

          // hide close button customily and help text upload image for custom cross word product or photo word product
          if (isCustomCrossWords.value || isCustomPhotoWord.value) {
            const helpText = customilyOptionWrapper.querySelector(
              HELP_TEXT_CUSTOMILY_SELECTOR
            )
            if (helpText) helpText.style.display = 'none'

            const clContainerCustomily = customilyOptionWrapper.querySelector(
              `.${CL_CONTAINER_CUSTOMILY_SELECTOR}`
            )
            if (clContainerCustomily) clContainerCustomily.style.display = 'none'
          }

          // create element and append to first index upload section customily
          const customeElement = document.createElement('div')
          customeElement.setAttribute(
            'id',
            `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${index}`
          )
          customeElement.setAttribute(
            'class',
            `${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}`
          )
          appendChildElementIntoParentElement(
            customilyElementWrapper,
            customeElement,
            0
          )
          await nextTick()

          // append component into DOM
          const element = document.querySelector(
            `#${SIB_POSITION_CUSTOM_PERSONALIZATION_PREFIX}-${index}`
          )
          if (!element) return
          
          const renderComponentStyleTransfer = () => {
            if (isProductHasTags(productStore.product, ['AI_multi_pose'])) {
              // Mount lần đầu: bọc trong reactive wrapper để type-switch cập nhật
              // props tại chỗ mà không đụng DOM (Customily sẽ không re-initialize).
              multiPoseTemplate.value = customizationTemplate
              const wrapper = defineComponent({
                setup() {
                  return () => h(CustomUploadImageStyleTransfer, {
                    id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
                    inputFileCustomily,
                    selectImageButtonCustomily,
                    removeImageButtonCustomily,
                    customizationTemplate: multiPoseTemplate.value,
                    optionSortId: multiPoseTemplate.value?.option_sort_id,
                    customName: multiPoseTemplate.value?.templates[0]?.custom_name,
                    customilyElementWrapper,
                    images: multiPoseTemplate.value?.templates[0]?.images,
                    custom: multiPoseTemplate.value?.templates[0]?.custom,
                    onlyText: multiPoseTemplate.value?.templates[0]?.only_text ? true : false,
                    isVideo: multiPoseTemplate.value?.templates[0]?.is_video ? true : false,
                  })
                }
              })
              const app = createApp(wrapper)
              Object.assign(app._context, appContext)
              app.mount(element)
              multiPoseApp = app
              return
            }

            try {
              Analytics.track('AI Customization Product Viewed', {
                productId: productStore.product?.id,
                productName: productStore.product?.title,
                optionSortId: customizationTemplate.option_sort_id,
              })
            } catch (err) {
              console.log('Error tracking AI Customization Product Viewed', err)
            }

            renderComponent({
              element,
              component: CustomUploadImageStyleTransfer,
              props: {
                id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
                inputFileCustomily,
                selectImageButtonCustomily,
                removeImageButtonCustomily,
                customizationTemplate,
                optionSortId: customizationTemplate.option_sort_id,
                customName: customizationTemplate?.templates[0]?.custom_name,
                customilyElementWrapper,
                images: customizationTemplate?.templates[0]?.images,
                custom: customizationTemplate?.templates[0]?.custom,
                onlyText: customizationTemplate?.templates[0]?.only_text ? true : false,
                isVideo: customizationTemplate?.templates[0]?.is_video ? true : false,
              },
              appContext,
            })
          }

          const renderComponentFaceSwap = () =>
            renderComponent({
              element,
              component: CustomUploadImageFaceSwap,
              props: {
                id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
                inputFileCustomily,
                selectImageButtonCustomily,
                optionSortId: customizationTemplate.option_sort_id,
                templates: customizationTemplate.templates,
              },
              appContext,
            })

          const renderComponentQrCodeSoundWave = () =>
            renderComponent({
              element,
              component: CustomUploadQrCodeSoundWave,
              props: {
                id: `${SIB_CUSTOM_UPLOAD_IMAGE_ID_PREFIX}-${index}`,
                customizationTemplate:
                  customizationTemplate.templates[0].extended_settings,
              },
              appContext,
            })

          const renderComponentCalander = () =>
            renderComponent({
              element,
              component: CustomCalendar,
              props: {
                dayIds: customizationTemplate.templates[0].day_ids,
                monthId: customizationTemplate.templates[0].month_id,
                yearId: customizationTemplate.templates[0].year_id,
              },
              appContext,
            })

          const renderComponentCrossWords = () =>
            renderComponent({
              element,
              component: CustomCrossWords,
              props: {
                inputFileCustomily,
                customizationTemplate: customizationTemplate.templates[0],
                customilyOptionWrapper,
              },
              appContext,
            })

          const renderComponentPhotoWord = () =>
            renderComponent({
              element,
              component: CustomPhotoWord,
              props: {
                inputFileCustomily,
                customizationTemplate: customizationTemplate.templates[0],
                customilyOptionWrapper,
                optionSortId: customizationTemplate.option_sort_id,
              },
              appContext,
            })

          if (numberCustomTag > 1) {
            if (
              isCustomStyleTransfer.value &&
              customizationTemplate.option_type == STYLE_TRANSFER_OPTION_TYPE
            ) {
              renderComponentStyleTransfer()
            }
            if (
              isCustomFaceSwap.value &&
              customizationTemplate.option_type == FACE_SWAP_OPTION_TYPE
            ) {
              renderComponentFaceSwap()
            }
            if (
              isCustomQrCodeSoundWave.value &&
              customizationTemplate.option_type == QRCODE_SOUND_OPTION_TYPE
            ) {
              renderComponentQrCodeSoundWave()
            }
            if (
              isCustomCalendar.value &&
              customizationTemplate.option_type == CALENDAR_OPTION_TYPE
            ) {
              renderComponentCalander()
            }

            if (
              isCustomCrossWords.value &&
              customizationTemplate.option_type == CROSS_WORDS_OPTION_TYPE
            ) {
              renderComponentCrossWords()
            }

            if (
              isCustomPhotoWord.value &&
              customizationTemplate.option_type == PHOTO_WORD_OPTION_TYPE
            ) {
              renderComponentPhotoWord()
            }
          } else {
            if (isCustomStyleTransfer.value) {
              renderComponentStyleTransfer()
            } else if (isCustomFaceSwap.value) {
              renderComponentFaceSwap()
            } else if (isCustomQrCodeSoundWave.value) {
              renderComponentQrCodeSoundWave()
            } else if (isCustomCalendar.value) {
              renderComponentCalander()
            } else if (isCustomCrossWords.value) {
              renderComponentCrossWords()
            } else if (isCustomPhotoWord.value) {
              renderComponentPhotoWord()
            }
          }
        }
        
        
      }
    )
  }

  async function convertImageLinkToFile(imageLink: string, fileName: string) {
    try {
      const response = await fetch(imageLink)
      const blob = await response.blob()
      const file = new File([blob], fileName, { type: 'image/png' })
      return file
    } catch (err) {
      Logger.log('Error converting URL image to input file:', err)
    }
  }

  async function convertImageLinkToFileList(
    imageLink: string,
    fileName: string
  ) {
    try {
      const file = await convertImageLinkToFile(imageLink, fileName)
      const files = new DataTransfer()
      files.items.add(file!)
      return files.files
    } catch (err) {
      Logger.log('Error converting URL image to input file list:', err)
    }
  }

  function changeFileImageCustomily(files?: FileList, inputElement?: any) {
    if (!files || !inputElement) return
    inputElement.files = files
    const event = new Event('change')
    inputElement.dispatchEvent(event)
  }

  function changeInputCustomily(value: string, inputElement?: any) {
    if (!inputElement) return
    inputElement.value = value
    const event = new Event('input')
    inputElement.dispatchEvent(event)
  }

  function changeOptionSelectCustomily(value: string, inputElement?: any) {
    if (!inputElement) return
    inputElement.value = value
    const event = new Event('change')
    inputElement.dispatchEvent(event)
  }

  function startUpload() {
    if (interval) {
      clearTimeout(interval)
    }
    percentProcessed.value = 0
    const oneSecondInMiliseconds = 1000
    interval = setInterval(run, oneSecondInMiliseconds)
    // const times = [20, 40, 60, 80, 100, 120, 140, 160] // list time in second
    const times = [4, 8, 12, 16, 20, 24, 28, 32] // list time in second
    const percents = [10, 20, 30, 40, 50, 60, 70, 80]
    let timeProcessing = 0

    function run() {
      if (timeProcessing > times.slice(-1)[0]) return
      timeProcessing += 1
      if (times.includes(timeProcessing)) {
        percentProcessed.value =
          percents.length > 1 ? percents.shift()! : percents.slice(-1)[0]
      }
    }
  }
  function finishUpload() {
    if (interval) {
      clearTimeout(interval)
    }
  }

  function getNunberCustomTag() {
    let numberCustomTag = 0
    numberCustomTag += isCustomStyleTransfer.value ? 1 : 0
    numberCustomTag += isCustomFaceSwap.value ? 1 : 0
    numberCustomTag += isCustomQrCodeSoundWave.value ? 1 : 0
    numberCustomTag += isCustomCalendar.value ? 1 : 0
    return numberCustomTag
  }

  function applyOptionSortOrder(wrapper: HTMLElement) {
    const options = wrapper.querySelectorAll('.customily_option')
    options.forEach(el => {
      const sortId = Number(el.getAttribute('sort-id'))
      if (!Number.isNaN(sortId)) {
        el.style.setProperty('--sort-order', sortId)
      }
    })
  }

  return {
    fetchCustomizationTemplates,
    fetchCustomizationTemplatesByCode,
    appendCustomUploadImage,
    convertImageLinkToFile,
    convertImageLinkToFileList,
    changeFileImageCustomily,
    changeInputCustomily,
    startUpload,
    finishUpload,
    percentProcessed,
    changeOptionSelectCustomily,
  }
}


